import {
	Component,
	OnInit,
	AfterViewInit,
	ViewChildren,
	QueryList,
	ElementRef,
	ViewChild,
	ChangeDetectorRef
} from "@angular/core";
import { FormGroup, Validators, FormBuilder } from "@angular/forms";
import { ActivatedRoute, Params } from "@angular/router";
import { ReCaptchaComponent } from "angular2-recaptcha/angular2-recaptcha";
import { DomSanitizer } from "@angular/platform-browser";

import { ValidationService } from "../../common/validation/validation.service";
import { AuthenticationService } from "../../common/authentication/authentication.service";
import { RegistrationService } from "../registration-service/registration.service";
import { BrowserTitleService } from "../../common/browser-title/browser-title.service";
import { ApiGatewayService } from "../../common/api-gateway/api-gateway.service";
import { LoginFormValue, LogoutState } from "../../models";
import { TranslateService } from "../../common/translate/translate.service";
import { SnackbarService } from "../../common/snackbar/snackbar.service";
import { environment } from "environments/environment";
import { getAccountName } from "../../util";
import { LoggedInUser } from "../../common/authentication/user";
import { NavigationService } from "../../common/navigation/navigation.service";

@Component({
	selector: "sdp-login-form",
	templateUrl: "./login-form.component.html",
	providers: [ValidationService]
})
export class LoginFormComponent implements OnInit, AfterViewInit {
	@ViewChildren("emailInput")
	private emailInput!: QueryList<ElementRef>;

	@ViewChildren("passwordInput")
	private passwordInput!: QueryList<ElementRef>;
	private accountName = getAccountName();

	@ViewChild(ReCaptchaComponent, { static: false })
	private recaptchaComponent!: ReCaptchaComponent;

	public loginForm: FormGroup;
	public mfaLoginForm: FormGroup;
	public isLoginSubmitted = false;
	public isResendSubmitted = false;
	public isConfirmationResent = false;
	public isNotConfirmed = false;
	public errMessage = "";
	public isVerified = false;
	public passwordType = "password";
	public forgot = false;
	public readonly passwordIcon = { type: "material", name: "remove_red_eye", cssClass: "sdp-password-show-icon" };
	public showMfaForm = false;

	private _recaptchaRequired = false;
	private recaptchaSuccessCode: string | undefined;

	private shouldClickOnPassword = false;

	constructor(
		private formBuilder: FormBuilder,
		private validationService: ValidationService,
		private auth: AuthenticationService,
		private route: ActivatedRoute,
		public registrationService: RegistrationService,
		private titleService: BrowserTitleService,
		private apiGatewayService: ApiGatewayService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService,
		private sanitizer: DomSanitizer,
		private changeDetectorRef: ChangeDetectorRef,
		private navigationService: NavigationService
	) {
		this.loginForm = this.formBuilder.group({
			email: ["", Validators.compose([Validators.required, this.validationService.email])],
			password: ["", Validators.required]
		});
		this.mfaLoginForm = this.formBuilder.group({
			totpCode: ["", Validators.compose([Validators.required, this.validationService.totpCode])]
		});
	}

	public async ngOnInit() {
		this.titleService.setTitle("registration.signIn.title");

		this.route.data.subscribe((v) => {
			this.isVerified = v.verify === "verify";
		});

		this.route.params.subscribe((params: Params) => {
			if (params["forgot"]) {
				this.forgot = true;
			}
		});

		this.route.queryParams.subscribe(async (params: Params) => {
			const email: string = params["email"];
			this.loginForm.controls["email"].setValue(email);
			if (email) {
				this.shouldClickOnPassword = true;
			}
		});

		// determine initial state of the captcha form
		this._recaptchaRequired = this.auth.isCaptchaRequiredFromCookie;

		// we might have navigated here because of a forced logout, which may include some state about why
		// if this is the special case where the user is disabled, we can present the error message
		const state: LogoutState | undefined = history.state;
		if (!!state && state.userDisabled) {
			this.errMessage = "error.message.login.disabled";
		}
	}

	public async ngAfterViewInit() {
		if (
			this.shouldClickOnPassword ||
			(this.route.snapshot.url.length > 0 && this.route.snapshot.url[0].path === "loginVerify")
		) {
			this.passwordInput.first.nativeElement.click();
		} else {
			this.emailInput.first.nativeElement.click();
		}
		this.changeDetectorRef.detectChanges();
		// Check if the user is already logged in
		if (this.auth.getCurrentUser()) {
			// Already logged in, forward them on
			await this.loginSucceeded(this.auth.getCurrentUser()!);
			return;
		}
	}

	public get recaptchaRequired() {
		return this._recaptchaRequired;
	}

	public get recaptchaSiteKey() {
		return environment.RECAPTCHA_SITE_KEY;
	}

	public recaptchaSuccess(successCode: string) {
		this.recaptchaSuccessCode = successCode;
	}

	public get recaptchaSucceeded() {
		if (this.recaptchaRequired) {
			return !!this.recaptchaSuccessCode;
		} else {
			return true;
		}
	}

	public get canSubmit() {
		if (this.registrationService.isLoaderVisible) {
			return false;
		}
		if (!this.recaptchaSucceeded) {
			return false;
		}
		return true;
	}

	public cancelLogin() {
		this.loginForm.reset();
		this.mfaLoginForm.reset();
		this.errMessage = "";
		this.showMfaForm = false;
	}

	// tslint:disable-next-line:cyclomatic-complexity
	public async login() {
		this.isConfirmationResent = false; // clear confirmation resent message
		if (this.loginForm.valid && this.recaptchaSucceeded && (!this.showMfaForm || this.mfaLoginForm.valid)) {
			const user = <LoginFormValue>this.loginForm.getRawValue();
			if (this.mfaLoginForm.valid) {
				user.totpCode = this.mfaLoginForm.getRawValue().totpCode;
			}
			if (this.recaptchaSuccessCode) {
				user.recaptchaCode = this.recaptchaSuccessCode;
			}
			this.registrationService.submitLoaderVisible(true);
			try {
				const result = await this.auth.login(user);
				const loggedInUser = this.auth.getCurrentUser();
				if (!result.result || !loggedInUser) {
					// only update recaptcha required when we fail a login
					// when we succeed we want to keep the field in it's previous state
					// this way, when we were showing a captcha, and have now succeeded, we don't vanish the captcha away while waiting for
					// the next page to render
					this._recaptchaRequired = result.captchaRequired;
					if (!!result.totpRequired) {
						this.showMfaForm = true; // triggers ui update
						this.errMessage = "";
					} else {
						this.errMessage = "error.message.invalidEmailOrPassword";
					}
				} else {
					const userData = await this.apiGatewayService.getSelf();
					loggedInUser.setFieldsFromUserData(userData);
					// Save the updated user with returned data
					this.auth.saveCurrentUser(loggedInUser);
					await this.loginSucceeded(loggedInUser, result.forcedPasswordChange);
				}
				// make sure next time we show the captcha it's ready to receive new clicks
				if (this.recaptchaComponent) {
					this.recaptchaComponent.reset();
				}
			} catch (e) {
				if (e.code === "UserNotFoundException") {
					this.errMessage = "error.message.invalidEmailOrPassword";
				} else if (e.code === "UserNotConfirmedException") {
					this.isNotConfirmed = true;
					this.errMessage = "message.account.isNotConfirmed";
				} else if (e.code === "UserLambdaValidationException" && e.message.indexOf("UserDisabled") >= 0) {
					this.errMessage = "error.message.login.disabled";
				} else if (e.code === "UserLambdaValidationException") {
					// nothing to do, we just don't want to show any error messages in this case
					// the login function in authentication service handled this for us
				} else if (e.code === "CodeMismatchException") {
					this.errMessage = "error.message.login.invalidMfaCode";
				} else {
					this.snackbarService.open(this.translateService.translate(e));
				}
			} finally {
				this.registrationService.submitLoaderVisible(false);
			}
		}
	}

	public openForgotPassword() {
		this.navigationService.navigate(["/forgotPassword"]);
	}

	public async resendConfirmation() {
		if (this.loginForm.valid && this.recaptchaSucceeded) {
			const user = <LoginFormValue>this.loginForm.getRawValue();
			if (this.recaptchaSuccessCode) {
				user.recaptchaCode = this.recaptchaSuccessCode;
			}
			this.registrationService.submitLoaderVisible(true);
			try {
				const result = await this.auth.resendRegistrationConfirmation(user.email);
				// make sure next time we show the captcha it's ready to receive new clicks
				if (this.recaptchaComponent) {
					this.recaptchaComponent.reset();
				}
				this.isConfirmationResent = true; // show message
				this.errMessage = "";
			} catch {
				this.errMessage = "error.message.confirmationEmail";
			} finally {
				this.registrationService.submitLoaderVisible(false);
				// restore login view
				this.isResendSubmitted = false;
				this.isNotConfirmed = false;
			}
		}
	}

	private async loginSucceeded(user: LoggedInUser, autoRedirect?: boolean) {
		if (user.admin) {
			if (user.hideWizard) {
				this.navigationService.navigate(["/administration/home"]);
			} else {
				this.navigationService.navigate(["/wizard/home"]);
			}
		} else {
			if (autoRedirect) {
				this.navigationService.navigate(["/download"], {
					queryParams: {
						autoRedirect: autoRedirect
					}
				});
			} else {
				this.navigationService.navigate(["/download"]);
			}
		}
	}
}
